import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import {
  TableHeader,
  TableContent,
  TableFooter,
  usePreventTableScrollingRef,
  TABLE_LAYOUT_OPTIONS,
  TableContainer,
  ROW_SELECTION,
  TablePagination,
  DEFAULT_ROWS_PER_PAGE_OPTIONS
} from 'cdk-radial';

interface AgGridProps {
  columns: any[];
  headerProps?: any;
  rowData?: any[];
  onRowClicked?: any ;
  getRowHeight?:any;
  dataPageSize?:any;
}

export const AgGrid = ({ 
  columns, 
  headerProps = {}, 
  rowData = [], 
  onRowClicked = () => {} ,
  getRowHeight ={},
  dataPageSize 
}: AgGridProps) => {
  const [gridApi, setGridApi] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);

  const tableContentRef = usePreventTableScrollingRef();
  const gridRef = useRef<AgGridReact>(null);
  const defaultPaginationPageSize = DEFAULT_ROWS_PER_PAGE_OPTIONS[0];

  const isAgGridReady = !!gridApi;
  const hasNoRows = totalRows === 0;

  const setPaginationValues = (api: any) => {
    if (!api) return;
    setCurrentPage(api.paginationGetCurrentPage() + 1 || 1);
    setTotalPages(api.paginationGetTotalPages() || 1);
    setTotalRows(api.getDisplayedRowCount() || 0);
  };

  const handleGridReady = useCallback((params: any) => {
    if (gridRef.current) {
      setGridApi(gridRef.current.api);
      setPaginationValues(gridRef.current.api);
    }
  }, []);

  const handleFirstDataRender = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit();
  }, []);

  const handlePaginationChanged = () => {
    if (!isAgGridReady) return;
    setPaginationValues(gridApi);
    gridApi.sizeColumnsToFit();
  };

  const paginationPageSize = () => {
    if (isAgGridReady) {
      return gridApi.paginationGetPageSize();
    }
    return defaultPaginationPageSize.value;
  };

  const handleTabbingFromPagination = (event: any) => {
    if (gridApi?.rowModel?.rowsToDisplay?.length > 0) {
      event.preventDefault();
      gridApi.setFocusedCell(totalRows - 1, columns[columns.length - 1]?.field);
    }
  };

  const getPageSummaryText = (currentPage: number = 1, totalPages: number = 1) => `Page ${currentPage} of ${totalPages}`;

  const getRowsSummaryText = (currentPage: number = 1, pageSize: number = 50, totalRows: number = 0) => {
    if (totalRows === 0) return `0-0 of 0`;
    const rowsFrom = (currentPage - 1) * pageSize + 1;
    const current = currentPage * pageSize;
    const rowsTo = Math.min(current, totalRows);
    return `${rowsFrom}-${rowsTo} of ${totalRows}`;
  };

  const paginationProps = {
    ariaLabel: 'Pagination',
    buttonLabels: {
      goToFirst: 'Go to first',
      goToLast: 'Go to last',
      goToNext: 'Go to next',
      goToPrevious: 'Go to previous'
    },
    className: '',
    dataTestId: `table-pagination`,
    defaultPaginationPageSize: defaultPaginationPageSize,
    gridApi,
    hideBorder: false,
    onTabbingIntoTable: handleTabbingFromPagination,
    pageSizeDropdownId: `page-size-dropdown-table`,
    pageSummaryLabelId: `pagination-summary-label-table`,
    pageSummaryText: getPageSummaryText(currentPage, totalPages),
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    rowsSummaryText: getRowsSummaryText(currentPage, paginationPageSize(), totalRows),
    showGoToFirstLastPage: true
  };

  const useSizeColumnsToFitOnResize = (gridRef: React.RefObject<AgGridReact>) => {
    useEffect(() => {
      const onResizeHandler = () => {
        if (gridRef.current?.api) {
          gridRef.current.api.sizeColumnsToFit();
        }
      };
      window.addEventListener('resize', onResizeHandler);
      return () => window.removeEventListener('resize', onResizeHandler);
    }, []);
  };

  useSizeColumnsToFitOnResize(gridRef);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [rowData]);

  return (
    <TableContainer agGridVersion="30.2.0">
      <TableHeader {...headerProps} hideBorder />
      <TableContent data-testid="table-content" ref={tableContentRef}  style={{ maxHeight:"352px", overflow: "auto" }} >
        <AgGridReact
          defaultColDef={{ filter: true, sortable: true }}
          columnDefs={columns}
          domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
          onFirstDataRendered={handleFirstDataRender}
          onGridReady={handleGridReady}
          rowData={rowData}
          rowSelection={ROW_SELECTION.MULTIPLE}
          suppressPaginationPanel
          pagination={true}
          ref={gridRef}
          onRowClicked={onRowClicked}
          paginationPageSize={dataPageSize}
          onPaginationChanged={handlePaginationChanged}
          getRowHeight={getRowHeight}
        />
      </TableContent>  
      <TableFooter hideBorder  style={{  overflow: "none" }}/>
      {isAgGridReady && <TablePagination {...paginationProps} hideBorder />} 
    
    </TableContainer>
  );
};
